import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { RolyPolySim } from '../sim/RolyPoly';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'roly-poly';
const MODEL_URL_DEFAULT = '/assets/roly-poly.glb';
const TUNE_ID = 'roly-poly';
const TUNE_DEFAULTS = { scale: 0.1, rotX: 0, rotY: 0, rotZ: 0, offY: 0 };

interface Avatar {
  root: THREE.Group;
  mixer: THREE.AnimationMixer;
  action: THREE.AnimationAction | null;
}

/** Animated roly-poly avatars backed by a shared skinned-mesh clone per sim entity. */
export class RolyPolyMesh {
  readonly group = new THREE.Group();
  private avatars: Avatar[] = [];
  private loaded = false;

  private readonly forward = new THREE.Vector3();
  private readonly up = new THREE.Vector3();

  constructor(private sim: RolyPolySim, private terrain: Terrain) {
    this.group.name = 'roly-polies';

    for (let i = 0; i < this.sim.rolies.length; i++) {
      const root = new THREE.Group();
      root.name = `roly-poly-${i}`;
      root.matrixAutoUpdate = false;
      root.visible = false;
      const mixer = new THREE.AnimationMixer(root);
      this.avatars.push({ root, mixer, action: null });
      this.group.add(root);
    }
    void this.load();
  }

  private async load(): Promise<void> {
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    } catch (err) {
      console.warn('[RolyPoly] GLB load failed:', err);
      return;
    }
    const src = gltf.scene;
    src.updateMatrixWorld(true);
    const tune = readTune(TUNE_ID, TUNE_DEFAULTS);
    const clip = gltf.animations[0] ?? null;

    for (let i = 0; i < this.avatars.length && i < this.sim.rolies.length; i++) {
      try {
        const av = this.avatars[i];
        const cloned = skeletonClone(src);
        cloned.scale.setScalar(tune.scale);
        cloned.position.set(0, 0, 0);
        cloned.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
        av.root.add(cloned);

        cloned.updateMatrixWorld(true);
        const bb = new THREE.Box3().setFromObject(cloned);
        cloned.position.y = -bb.min.y + tune.offY;
        cloned.updateMatrixWorld(true);

        cloned.traverse((o) => {
          const m = o as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
          m.frustumCulled = false;
        });

        const mixer = new THREE.AnimationMixer(cloned);
        let action: THREE.AnimationAction | null = null;
        if (clip) {
          action = mixer.clipAction(clip);
          action.play();
          action.time = Math.random() * clip.duration;
        }
        av.mixer = mixer;
        av.action = action;
      } catch (err) {
        console.error(`[RolyPoly] clone ${i} crashed:`, err);
      }
    }
    this.loaded = true;
  }

  update(dt: number): void {
    if (!this.loaded) return;
    const rolies = this.sim.rolies;
    for (let i = 0; i < rolies.length; i++) {
      const r = rolies[i];
      const av = this.avatars[i];
      if (!av) continue;
      av.root.visible = true;
      this.writePose(av.root, r);
      // Freeze legs during the pre-turn pause; walk at full tempo otherwise.
      if (av.action) av.action.timeScale = r.mode === 'walk' ? 0.9 : 0;
      av.mixer.update(dt);
    }
  }

  private writePose(root: THREE.Group, r: { x: number; y: number; z: number; heading: number }): void {
    this.terrain.getNormal(r.x, r.z, this.up);
    this.forward.set(Math.cos(r.heading), 0, Math.sin(r.heading));
    this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    if (this.forward.lengthSq() < 1e-6) {
      this.forward.set(1, 0, 0);
      this.forward.addScaledVector(this.up, -this.forward.dot(this.up));
    }
    this.forward.normalize();

    const bx = this.forward.y * this.up.z - this.forward.z * this.up.y;
    const by = this.forward.z * this.up.x - this.forward.x * this.up.z;
    const bz = this.forward.x * this.up.y - this.forward.y * this.up.x;

    const e = root.matrix.elements;
    e[0] = this.forward.x; e[1] = this.forward.y; e[2] = this.forward.z; e[3] = 0;
    e[4] = this.up.x;      e[5] = this.up.y;      e[6] = this.up.z;      e[7] = 0;
    e[8] = bx;             e[9] = by;             e[10] = bz;            e[11] = 0;
    e[12] = r.x;           e[13] = r.y + 0.003;   e[14] = r.z;           e[15] = 1;
    root.matrixWorldNeedsUpdate = true;
  }
}
